import React, { useEffect, useState } from 'react';
import { authFetch } from '@/lib/auth';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Switch } from '@/components/ui/switch';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle, DialogFooter } from '@/components/ui/dialog';
import { Loader2, CalendarCheck } from 'lucide-react';
import { toast } from 'sonner';

const API_BASE = process.env.REACT_APP_BACKEND_URL;

const DURATIONS = [
  { value: '30', label: '30 min' },
  { value: '60', label: '1 hour' },
  { value: '90', label: '1.5 hours' },
  { value: '120', label: '2 hours' },
  { value: '180', label: '3 hours' },
  { value: '240', label: '4 hours' },
];

const WINDOW_START = { morning: '09:00', midday: '11:00', afternoon: '13:00', evening: '16:00', twilight: '18:00' };

export const ScheduleShootModal = ({ open, onOpenChange, orderId, mode = 'schedule', initial = {}, onDone }) => {
  const [date, setDate] = useState('');
  const [time, setTime] = useState('10:00');
  const [duration, setDuration] = useState('90');
  const [note, setNote] = useState('');
  const [sendEmail, setSendEmail] = useState(true);
  const [saving, setSaving] = useState(false);
  const isReschedule = mode === 'reschedule';

  useEffect(() => {
    if (!open) return;
    setDate(initial.date || '');
    setTime(initial.time || WINDOW_START[initial.timeWindow] || '10:00');
    setDuration(String(initial.duration || 90));
    setNote(initial.note || '');
    setSendEmail(true);
  }, [open]);

  const submit = async () => {
    if (!date || !time) {
      toast.error('Please choose a shoot date and start time');
      return;
    }
    setSaving(true);
    try {
      const res = await authFetch(`${API_BASE}/api/scheduling/orders/${orderId}/${isReschedule ? 'reschedule' : 'schedule'}`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          shoot_date: date,
          start_time: time,
          duration_minutes: parseInt(duration, 10),
          note_to_client: note.trim() || null,
          send_email: sendEmail,
        }),
      });
      const data = await res.json();
      if (!res.ok) throw new Error(data.detail || 'Failed to save schedule');
      const sent = data.emails_sent?.client ? ' — invite emailed to client' : '';
      toast.success(`${isReschedule ? 'Shoot rescheduled' : 'Shoot scheduled'}${sent}`);
      onOpenChange(false);
      onDone?.();
    } catch (err) {
      toast.error(err.message);
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="bg-[#0f1724] border-white/10 text-white sm:max-w-md" data-testid="schedule-shoot-modal">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2 font-light tracking-wide">
            <CalendarCheck className="h-5 w-5 text-[#C6A14A]" />
            {isReschedule ? 'Reschedule Shoot' : 'Schedule Shoot'}
          </DialogTitle>
          <DialogDescription className="text-[#E8E4DE]/60">
            Times are Arizona time (America/Phoenix). A calendar invite is attached to the emails.
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-4 py-2">
          <div className="grid grid-cols-2 gap-3">
            <div className="space-y-1.5">
              <Label className="text-[#E8E4DE]/80">Shoot date</Label>
              <Input type="date" value={date} onChange={(e) => setDate(e.target.value)}
                className="bg-[#050816] border-white/10 text-white [color-scheme:dark]" data-testid="schedule-date-input" />
            </div>
            <div className="space-y-1.5">
              <Label className="text-[#E8E4DE]/80">Start time</Label>
              <Input type="time" step="900" value={time} onChange={(e) => setTime(e.target.value)}
                className="bg-[#050816] border-white/10 text-white [color-scheme:dark]" data-testid="schedule-time-input" />
            </div>
          </div>
          <div className="space-y-1.5">
            <Label className="text-[#E8E4DE]/80">Duration</Label>
            <Select value={duration} onValueChange={setDuration}>
              <SelectTrigger className="bg-[#050816] border-white/10 text-white" data-testid="schedule-duration-select">
                <SelectValue />
              </SelectTrigger>
              <SelectContent className="bg-[#0f1724] border-white/10 text-white">
                {DURATIONS.map((d) => (
                  <SelectItem key={d.value} value={d.value} data-testid={`schedule-duration-${d.value}`}>{d.label}</SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
          <div className="space-y-1.5">
            <Label className="text-[#E8E4DE]/80">Note to client <span className="text-[#E8E4DE]/40">(optional)</span></Label>
            <Textarea value={note} maxLength={500} onChange={(e) => setNote(e.target.value)} rows={3}
              placeholder="e.g. Please have all lights on when we arrive"
              className="bg-[#050816] border-white/10 text-white" data-testid="schedule-note-input" />
          </div>
          <div className="flex items-center justify-between rounded-lg border border-white/10 bg-[#050816] px-3 py-2.5">
            <div>
              <p className="text-sm text-white">Email client & admin</p>
              <p className="text-xs text-[#E8E4DE]/50">Sends confirmation with calendar invite</p>
            </div>
            <Switch checked={sendEmail} onCheckedChange={setSendEmail} data-testid="schedule-send-email-toggle" />
          </div>
        </div>

        <DialogFooter className="gap-2">
          <Button variant="outline" onClick={() => onOpenChange(false)} disabled={saving}
            className="border-white/10 text-[#E8E4DE] bg-transparent hover:bg-white/5" data-testid="schedule-cancel-btn">
            Close
          </Button>
          <Button onClick={submit} disabled={saving}
            className="bg-[#C6A14A] text-[#050816] hover:bg-[#d4b25c]" data-testid="schedule-submit-btn">
            {saving && <Loader2 className="h-4 w-4 mr-2 animate-spin" />}
            {isReschedule ? 'Save New Time' : 'Confirm Schedule'}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};
